// INTELIMOVE-SAAS-005 (IM-ADR-023): motor comercial — catálogo técnico de funcionalidades comercializáveis, regras de
// decisão (plano, legado, limites, créditos), períodos e validações. Puro: usado pela API (decisão real), pela
// administração (prévia e diferenças) e pelas telas (só exibição). Nenhum preço existe aqui; nenhum limite é inventado:
// sem plano (LEGADO) valem exatamente as regras que o produto já aplicava.
import { z } from 'zod';

export const FEATURE_CATEGORIES = ['INTELIGENCIA_COMERCIAL', 'ESTRATEGIA', 'PLANEJAMENTO', 'INTEGRACOES'] as const;
export type FeatureCategory = (typeof FEATURE_CATEGORIES)[number];
export const FEATURE_CATEGORY_LABEL: Record<FeatureCategory, string> = {
  INTELIGENCIA_COMERCIAL: 'Inteligência comercial', ESTRATEGIA: 'Estratégia', PLANEJAMENTO: 'Planejamento', INTEGRACOES: 'Integrações',
};

/** OPERACAO = execução medida (custo variável); MODULO = área do produto; CAPACIDADE = quantidade simultânea. */
export const FEATURE_KINDS = ['OPERACAO', 'MODULO', 'CAPACIDADE'] as const;
export type FeatureKind = (typeof FEATURE_KINDS)[number];
export const FEATURE_MODES = ['DISPONIVEL', 'INDISPONIVEL', 'LIMITADO', 'CREDITOS'] as const;
export type FeatureMode = (typeof FEATURE_MODES)[number];
export const FEATURE_MODE_LABEL: Record<FeatureMode, string> = {
  DISPONIVEL: 'Disponível', INDISPONIVEL: 'Indisponível', LIMITADO: 'Disponível com limite', CREDITOS: 'Disponível mediante créditos',
};
export const LIMIT_PERIODS = ['DIA', 'MES', 'SIMULTANEO'] as const;
export type LimitPeriod = (typeof LIMIT_PERIODS)[number];
export const LIMIT_PERIOD_LABEL: Record<LimitPeriod, string> = { DIA: 'por dia', MES: 'por mês', SIMULTANEO: 'ao mesmo tempo' };

/** Modos e períodos aceitos por tipo (a publicação recusa combinações fora daqui). */
export const KIND_RULES: Record<FeatureKind, { modes: readonly FeatureMode[]; periods: readonly LimitPeriod[] }> = {
  OPERACAO: { modes: FEATURE_MODES, periods: ['DIA', 'MES'] },
  MODULO: { modes: ['DISPONIVEL', 'INDISPONIVEL'], periods: [] },
  CAPACIDADE: { modes: ['DISPONIVEL', 'INDISPONIVEL', 'LIMITADO'], periods: ['SIMULTANEO'] },
};

/**
 * Regra que o produto já aplicava antes dos planos (LEGADO). `limitKey` lê `tenant_subscriptions.entitlements`;
 * `period` preserva o relógio original de cada contador: UTC no código (`new Date().toISOString()`) ou a data da sessão
 * do banco (`current_date`, usado pelo Analista de Mercado).
 */
export interface LegacyRule {
  flagKey?: string;
  flagMessage?: string;
  limitKey?: string;
  /** Valor quando a chave falta. `null` com `integerOnly` = zero (regra original do Analista de Mercado). */
  defaultLimit?: number | null;
  integerOnly?: boolean;
  metric?: string;
  period?: 'UTC_DIA' | 'BANCO_DIA';
  /** Mensagem original de limite atingido (preservada palavra por palavra). */
  limitMessage?: (limit: number) => string;
  /** Sem registro de assinatura a operação é recusada (regra original do Analista de Mercado). */
  requiresSubscription?: boolean;
}

export interface CommercialFeature {
  key: string;
  category: FeatureCategory;
  kind: FeatureKind;
  label: string;
  /** Substantivo no plural para mensagens de limite ("pesquisas de concorrentes"). */
  noun: string;
  description: string;
  /** O que gera custo variável. NENHUM = sem custo variável relevante conhecido. */
  costDriver: 'IA' | 'COLETA_EXTERNA' | 'NENHUM';
  unit: 'EXECUCAO' | 'LOTE' | 'ITEM';
  /** Instrumentado nesta versão (decisão + registro de consumo no caminho real). */
  instrumented: boolean;
  /** Prefixos de rota cujas escritas pertencem ao módulo (só MODULO). */
  routes?: readonly string[];
  legacy: LegacyRule;
}

/**
 * IM-CAP-044: catálogo técnico. Cada chave é estável e corresponde a um ponto real do código; espelhado na tabela
 * `commercial_features` (IM-MIG-040) — o teste de paridade IM-TEST-1475 confere os dois.
 */
export const COMMERCIAL_FEATURES: readonly CommercialFeature[] = [
  { key: 'company_site_analysis', category: 'INTELIGENCIA_COMERCIAL', kind: 'OPERACAO', label: 'Pesquisa da própria empresa (análise do site)', noun: 'análises do site da empresa',
    description: 'Coleta do site e síntese por IA do contexto da empresa contratante.', costDriver: 'IA', unit: 'EXECUCAO', instrumented: true,
    legacy: { limitKey: 'site_analyses_per_day', defaultLimit: 10, metric: 'site_analysis', period: 'UTC_DIA', limitMessage: (n) => `Limite diário de ${n} análises de site atingido.` } },
  { key: 'competitor_research', category: 'INTELIGENCIA_COMERCIAL', kind: 'OPERACAO', label: 'Pesquisa de concorrente', noun: 'pesquisas de concorrentes',
    description: 'Pesquisa de um concorrente com coleta pública e síntese por IA.', costDriver: 'IA', unit: 'EXECUCAO', instrumented: true,
    legacy: { limitKey: 'competitor_researches_per_day', defaultLimit: 30, metric: 'competitor_research', period: 'UTC_DIA', limitMessage: (n) => `Limite diário de ${n} pesquisas de concorrentes atingido. Tente amanhã.` } },
  { key: 'target_research', category: 'INTELIGENCIA_COMERCIAL', kind: 'OPERACAO', label: 'Pesquisa de empresa-alvo', noun: 'pesquisas de empresas',
    description: 'Pesquisa de uma empresa-alvo com coleta pública e análise de aderência por IA.', costDriver: 'IA', unit: 'EXECUCAO', instrumented: true,
    legacy: { limitKey: 'target_researches_per_day', defaultLimit: 30, metric: 'target_research', period: 'UTC_DIA', limitMessage: (n) => `Limite diário de ${n} pesquisas de empresas-alvo atingido. Tente amanhã.` } },
  { key: 'target_discovery', category: 'INTELIGENCIA_COMERCIAL', kind: 'OPERACAO', label: 'Encontrar empresas (lotes de sugestões)', noun: 'buscas de empresas',
    description: 'Lote de até 10 sugestões de empresas aderentes ao ICP (busca externa e IA).', costDriver: 'IA', unit: 'LOTE', instrumented: true, legacy: {} },
  { key: 'open_data_discovery', category: 'INTELIGENCIA_COMERCIAL', kind: 'OPERACAO', label: 'Descoberta em dados abertos', noun: 'descobertas em dados abertos',
    description: 'Descoberta de empresas em bases públicas (CNPJ, compras públicas, CVM).', costDriver: 'COLETA_EXTERNA', unit: 'EXECUCAO', instrumented: true, legacy: {} },
  { key: 'people_discovery', category: 'INTELIGENCIA_COMERCIAL', kind: 'OPERACAO', label: 'Busca de pessoas públicas', noun: 'buscas de pessoas',
    description: 'Busca de pessoas públicas de uma empresa-alvo em fontes abertas.', costDriver: 'COLETA_EXTERNA', unit: 'EXECUCAO', instrumented: true, legacy: {} },
  { key: 'market_research', category: 'INTELIGENCIA_COMERCIAL', kind: 'OPERACAO', label: 'Analista de Mercado (concorrência e SWOT)', noun: 'pesquisas de mercado',
    description: 'Execução do Analista de Mercado: concorrentes, comparação, achados e SWOT por IA.', costDriver: 'IA', unit: 'EXECUCAO', instrumented: true,
    legacy: { flagKey: 'market_intelligence', flagMessage: 'A Inteligência de Mercado não está habilitada para esta empresa.', limitKey: 'market_runs_per_day', defaultLimit: null,
      integerOnly: true, metric: 'market_runs', period: 'BANCO_DIA', requiresSubscription: true,
      limitMessage: (n) => `Limite de ${n} execução(ões) de pesquisa por dia atingido. Tente novamente amanhã.` } },
  { key: 'commercial_map_ai', category: 'INTELIGENCIA_COMERCIAL', kind: 'OPERACAO', label: 'Mapa comercial com IA', noun: 'gerações do mapa comercial com IA',
    description: 'Geração do mapa comercial com a etapa de IA (a versão sem IA não é medida).', costDriver: 'IA', unit: 'EXECUCAO', instrumented: true, legacy: {} },
  { key: 'monitored_sources', category: 'INTELIGENCIA_COMERCIAL', kind: 'CAPACIDADE', label: 'Fontes monitoradas ativas', noun: 'fontes monitoradas',
    description: 'Fontes (sites, feeds, canais) em monitoramento ativo ao mesmo tempo.', costDriver: 'COLETA_EXTERNA', unit: 'ITEM', instrumented: true, legacy: {} },
  { key: 'competitors_tracked', category: 'INTELIGENCIA_COMERCIAL', kind: 'CAPACIDADE', label: 'Concorrentes acompanhados', noun: 'concorrentes acompanhados',
    description: 'Concorrentes confirmados ao mesmo tempo (sugestões não contam).', costDriver: 'NENHUM', unit: 'ITEM', instrumented: true, legacy: {} },
  { key: 'battlecards', category: 'INTELIGENCIA_COMERCIAL', kind: 'MODULO', label: 'Battlecards', noun: 'battlecards',
    description: 'Battlecards por concorrente, validação de itens e monitoramento ligado.', costDriver: 'NENHUM', unit: 'ITEM', instrumented: true, routes: ['/api/v1/battlecards'], legacy: {} },
  { key: 'segmentation', category: 'ESTRATEGIA', kind: 'MODULO', label: 'Segmentação', noun: 'segmentações',
    description: 'Segmentação e ranking de segmentos a partir do contexto e dos ICPs.', costDriver: 'NENHUM', unit: 'ITEM', instrumented: true, routes: ['/api/v1/segmentation'], legacy: {} },
  { key: 'market_sizing', category: 'ESTRATEGIA', kind: 'MODULO', label: 'TAM/SAM/SOM', noun: 'dimensionamentos',
    description: 'Dimensionamento de mercado com estatísticas oficiais.', costDriver: 'NENHUM', unit: 'ITEM', instrumented: true, routes: ['/api/v1/market-sizing'], legacy: {} },
  { key: 'environment_analysis', category: 'ESTRATEGIA', kind: 'MODULO', label: 'PESTEL e Cinco Forças de Porter', noun: 'análises de ambiente',
    description: 'Análise de ambiente (PESTEL) e das Cinco Forças.', costDriver: 'NENHUM', unit: 'ITEM', instrumented: true, routes: ['/api/v1/environment'], legacy: {} },
  { key: 'portfolio', category: 'ESTRATEGIA', kind: 'MODULO', label: 'Portfólio, preços e ecossistema', noun: 'análises de portfólio',
    description: 'Portfólio, séries de preço e relações do ecossistema.', costDriver: 'NENHUM', unit: 'ITEM', instrumented: true, routes: ['/api/v1/portfolio'], legacy: {} },
  { key: 'strategic_planning', category: 'PLANEJAMENTO', kind: 'MODULO', label: 'Planejamento estratégico e metas', noun: 'planos',
    description: 'Plano do ano, metas mensais, importações e acompanhamento previsto × realizado.', costDriver: 'NENHUM', unit: 'ITEM', instrumented: true, routes: ['/api/v1/planning'], legacy: {} },
  { key: 'scenarios', category: 'PLANEJAMENTO', kind: 'MODULO', label: 'Cenários', noun: 'simulações',
    description: 'Simulação de cenários e vínculo com decisões.', costDriver: 'NENHUM', unit: 'ITEM', instrumented: true, routes: ['/api/v1/scenarios'], legacy: {} },
  { key: 'executive_decisions', category: 'PLANEJAMENTO', kind: 'MODULO', label: 'Parecer executivo e Central de Decisões', noun: 'pareceres',
    description: 'Parecer executivo consolidado, Assistente de Decisão, Radar e registro de decisões.', costDriver: 'NENHUM', unit: 'ITEM', instrumented: true, routes: ['/api/v1/executive', '/api/v1/decisions', '/api/v1/opportunities', '/api/v1/decision-brief', '/api/v1/opportunity-radar'], legacy: {} },
  { key: 'learning', category: 'PLANEJAMENTO', kind: 'MODULO', label: 'Aprendizado e resultados', noun: 'registros de aprendizado',
    description: 'Resultados observados, comparação com o esperado e ajustes.', costDriver: 'NENHUM', unit: 'ITEM', instrumented: true, routes: ['/api/v1/learning', '/api/v1/results', '/api/v1/benchmarks'], legacy: {} },
  { key: 'crm_integrations', category: 'INTEGRACOES', kind: 'MODULO', label: 'Integração com CRM', noun: 'integrações',
    description: 'Conexão com CRM externo, entregas e reconciliação.', costDriver: 'NENHUM', unit: 'ITEM', instrumented: true, routes: ['/api/v1/integrations', '/api/v1/crm-deliveries'], legacy: {} },
  { key: 'active_integrations', category: 'INTEGRACOES', kind: 'CAPACIDADE', label: 'Integrações ativas', noun: 'integrações ativas',
    description: 'Conexões de CRM e conectores de planejamento ativos ao mesmo tempo.', costDriver: 'NENHUM', unit: 'ITEM', instrumented: true, legacy: {} },
];

export type CommercialFeatureKey = string;
export const FEATURE_BY_KEY: Readonly<Record<string, CommercialFeature>> = Object.freeze(Object.fromEntries(COMMERCIAL_FEATURES.map((f) => [f.key, f])));
export const featureOf = (key: string): CommercialFeature => {
  const f = FEATURE_BY_KEY[key];
  if (!f) throw new Error(`funcionalidade comercial desconhecida: ${key}`);
  return f;
};
/** Código gravado em `usage_records.feature` (maiúsculas; TARGET_DISCOVERY já existia assim). */
export const usageFeatureCode = (key: string) => key.toUpperCase();

// ── Planos e assinaturas ────────────────────────────────────────────────────────────────────────────────────────────
export const COMMERCIAL_PLAN_CATEGORIES = ['BASIC', 'PRO', 'INTELLIGENCE', 'PERSONALIZADO'] as const;
export type CommercialPlanCategory = (typeof COMMERCIAL_PLAN_CATEGORIES)[number];
export const COMMERCIAL_PLAN_CATEGORY_LABEL: Record<CommercialPlanCategory, string> = { BASIC: 'Basic', PRO: 'Pro', INTELLIGENCE: 'Intelligence', PERSONALIZADO: 'Personalizado' };
/** Situação do plano para NOVAS contratações (contratos vigentes nunca mudam por aqui). */
export const COMMERCIAL_PLAN_STATUSES = ['ATIVO', 'DESCONTINUADO'] as const;
export type CommercialPlanStatus = (typeof COMMERCIAL_PLAN_STATUSES)[number];
export const COMMERCIAL_PLAN_VERSION_STATUSES = ['RASCUNHO', 'PUBLICADA'] as const;
export type CommercialPlanVersionStatus = (typeof COMMERCIAL_PLAN_VERSION_STATUSES)[number];

/** Situação COMERCIAL da assinatura — independente da situação operacional (suspensão de segurança) da organização. */
export const COMMERCIAL_STATUSES = ['LEGADO', 'PENDENTE', 'AVALIACAO', 'ATIVA', 'SUSPENSA', 'CANCELADA', 'EXPIRADA'] as const;
export type CommercialStatus = (typeof COMMERCIAL_STATUSES)[number];
export const COMMERCIAL_STATUS_LABEL: Record<CommercialStatus, string> = {
  LEGADO: 'Legado', PENDENTE: 'Contratação pendente', AVALIACAO: 'Em avaliação', ATIVA: 'Ativa', SUSPENSA: 'Suspensa comercialmente', CANCELADA: 'Cancelada', EXPIRADA: 'Expirada',
};
/** Estados em que operações medidas são recusadas (o histórico continua legível). */
export const COMMERCIAL_BLOCKING: readonly CommercialStatus[] = ['PENDENTE', 'SUSPENSA', 'CANCELADA', 'EXPIRADA'];

export interface FeatureRule { mode: FeatureMode; limit: number | null; period: LimitPeriod | null; credits: number | null }
export interface CreditPolicy { creditsPerPeriod: number | null; expireAtPeriodEnd: boolean }

export interface CommercialPlanVersionView {
  id: string;
  planId: string;
  planCode: string;
  planName: string;
  versionNo: number;
  seatsIncluded: number | null;
  seatsUnlimited: boolean;
  creditPolicy: CreditPolicy;
  features: Record<string, FeatureRule>;
}

/** Estado comercial lido sob a organização ativa (insumo de toda decisão). */
export interface SubscriptionState {
  exists: boolean;
  legacyStatus: string | null;
  commercialStatus: CommercialStatus;
  endsAt: string | null;
  seatsContracted: number | null;
  legacyEntitlements: Record<string, unknown>;
  plan: CommercialPlanVersionView | null;
}

export function effectiveCommercialStatus(s: Pick<SubscriptionState, 'commercialStatus' | 'endsAt'>, now = new Date()): CommercialStatus {
  if ((s.commercialStatus === 'ATIVA' || s.commercialStatus === 'AVALIACAO') && s.endsAt && new Date(s.endsAt) <= now) return 'EXPIRADA';
  return s.commercialStatus;
}

export interface ResolvedRule extends FeatureRule {
  origin: 'LEGADO' | 'PLANO';
  /** Contador usado na decisão (LEGADO = métrica histórica; PLANO = métrica própria por período). */
  metric: string | null;
  /** Relógio do período: UTC_DIA/BANCO_DIA (legado) ou SP (America/Sao_Paulo) nos planos. */
  clock: 'UTC_DIA' | 'BANCO_DIA' | 'SP' | null;
  /** Motivo quando INDISPONIVEL. */
  reason: string | null;
}

const legacyLimit = (f: CommercialFeature, ent: Record<string, unknown>): number | null => {
  const k = f.legacy.limitKey;
  if (!k) return null;
  const v = ent[k];
  if (f.legacy.integerOnly) return Number.isInteger(v) ? Number(v) : 0;
  return Number(v ?? f.legacy.defaultLimit ?? 0);
};

/** Regra efetiva de uma funcionalidade para a organização (sem plano = regra LEGADO, idêntica à anterior). */
export function resolveRule(sub: SubscriptionState, key: string): ResolvedRule {
  const f = featureOf(key);
  if (!sub.plan) {
    const ent = sub.legacyEntitlements ?? {};
    if (f.legacy.flagKey && ent[f.legacy.flagKey] !== true) {
      return { mode: 'INDISPONIVEL', limit: null, period: null, credits: null, origin: 'LEGADO', metric: null, clock: null, reason: f.legacy.flagMessage ?? null };
    }
    const limit = legacyLimit(f, ent);
    if (limit !== null) return { mode: 'LIMITADO', limit, period: 'DIA', credits: null, origin: 'LEGADO', metric: f.legacy.metric ?? null, clock: f.legacy.period ?? 'UTC_DIA', reason: null };
    return { mode: 'DISPONIVEL', limit: null, period: null, credits: null, origin: 'LEGADO', metric: null, clock: null, reason: null };
  }
  const r = sub.plan.features[key];
  // Funcionalidade nova no catálogo e ausente de uma versão já publicada: fail-closed até uma versão nova decidir.
  if (!r) return { mode: 'INDISPONIVEL', limit: null, period: null, credits: null, origin: 'PLANO', metric: null, clock: null, reason: `${f.label} não está incluída no plano da sua empresa.` };
  const periodic = r.mode === 'LIMITADO' && (r.period === 'DIA' || r.period === 'MES');
  return {
    ...r, origin: 'PLANO', clock: periodic ? 'SP' : null, metric: periodic ? planMetric(key, r.period as 'DIA' | 'MES') : null,
    reason: r.mode === 'INDISPONIVEL' ? `${f.label} não está incluída no plano da sua empresa.` : null,
  };
}

/** Métrica do contador de plano (`usage_counters.metric`): nunca colide com as métricas legadas. */
export const planMetric = (key: string, period: 'DIA' | 'MES') => `plano_${key}_${period === 'DIA' ? 'dia' : 'mes'}`;

// ── Períodos (IM-NFR: fuso explícito) ──────────────────────────────────────────────────────────────────────────────
/** Fuso oficial dos períodos comerciais. O Brasil não tem horário de verão desde 2019: offset fixo −03:00. */
export const COMMERCIAL_TIMEZONE = 'America/Sao_Paulo';
const SP_OFFSET = '-03:00';

function spParts(now: Date): { y: number; m: number; d: number } {
  const p = new Intl.DateTimeFormat('en-CA', { timeZone: COMMERCIAL_TIMEZONE, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
  const v = (t: string) => Number(p.find((x) => x.type === t)!.value);
  return { y: v('year'), m: v('month'), d: v('day') };
}
const ymd = (y: number, m: number, d: number) => `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

/** Janela do período em America/Sao_Paulo: início (data local, chave do contador) e renovação (instante ISO). */
export function periodWindow(period: 'DIA' | 'MES', now = new Date()): { start: string; renewsAt: string } {
  const { y, m, d } = spParts(now);
  if (period === 'DIA') {
    const next = new Date(Date.UTC(y, m - 1, d + 1));
    return { start: ymd(y, m, d), renewsAt: new Date(`${ymd(next.getUTCFullYear(), next.getUTCMonth() + 1, next.getUTCDate())}T00:00:00${SP_OFFSET}`).toISOString() };
  }
  const ny = m === 12 ? y + 1 : y; const nm = m === 12 ? 1 : m + 1;
  return { start: ymd(y, m, 1), renewsAt: new Date(`${ymd(ny, nm, 1)}T00:00:00${SP_OFFSET}`).toISOString() };
}

/** Renovação dos contadores legados (dia UTC). O relógio do banco (BANCO_DIA) depende do fuso da sessão do banco. */
export function legacyRenewsAt(now = new Date()): string {
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1)).toISOString();
}

// ── Mensagens e decisão ───────────────────────────────────────────────────────────────────────────────────────────
export const COMMERCIAL_MESSAGES = {
  subscription_inactive_legacy: 'A assinatura desta empresa não está ativa.',
  subscription_missing: 'Assinatura não encontrada para esta empresa.',
  insufficient_credits: 'Sua empresa não tem créditos suficientes para esta operação. Fale com o responsável pela conta para ampliar o saldo.',
  seat_limit_reached: 'Sua organização atingiu o limite de usuários contratados. Desative um acesso ou revogue um convite pendente para liberar espaço; a ampliação de usuários é feita pelo responsável pela conta.',
} as const;

export function blockedMessage(status: CommercialStatus): string {
  const word = status === 'SUSPENSA' ? 'suspensa comercialmente' : status === 'CANCELADA' ? 'cancelada' : 'expirada';
  return `A assinatura desta empresa está ${word}. O histórico continua disponível para leitura; fale com o responsável pela conta.`;
}

export function quotaMessage(rule: ResolvedRule, f: CommercialFeature): string {
  if (rule.origin === 'LEGADO' && f.legacy.limitMessage) return f.legacy.limitMessage(rule.limit ?? 0);
  if (rule.period === 'SIMULTANEO') return `Você atingiu o limite de ${f.noun} do seu plano. Encerre ou arquive um item para incluir outro.`;
  return `Você atingiu o limite de ${f.noun} do seu plano neste período.`;
}

export type DecisionCode = 'subscription_inactive' | 'feature_unavailable' | 'quota_exceeded' | 'insufficient_credits' | 'not_entitled';

export interface CommercialDecision {
  allowed: boolean;
  code: DecisionCode | null;
  status: number;
  message: string | null;
  rule: ResolvedRule;
  commercialStatus: CommercialStatus;
}

/**
 * IM-SEC-127: decisão comercial (posição 3–5 da ordem: assinatura → recurso → limite → créditos; permissão do usuário e
 * situação operacional são checadas antes, fora daqui). `used` e `available` vêm do banco; o consumo atômico é SQL.
 */
export function decide(sub: SubscriptionState, key: string, usage: { used: number } | null, credits: { available: number } | null, now = new Date()): CommercialDecision {
  const f = featureOf(key);
  const rule = resolveRule(sub, key);
  const status = effectiveCommercialStatus(sub, now);
  const out = (allowed: boolean, code: DecisionCode | null, http: number, message: string | null): CommercialDecision => ({ allowed, code, status: http, message, rule, commercialStatus: status });
  // Analista de Mercado no LEGADO: toda recusa é `not_entitled` (429 no limite, 403 nos demais) — contrato original.
  const legacyGate = f.legacy.requiresSubscription && !sub.plan;
  const deny = (code: DecisionCode, http: number, message: string) => legacyGate ? out(false, 'not_entitled', code === 'quota_exceeded' ? 429 : 403, message) : out(false, code, http, message);
  if (!sub.exists && legacyGate) return out(false, 'not_entitled', 403, COMMERCIAL_MESSAGES.subscription_missing);
  if (sub.legacyStatus === 'SUSPENDED' || sub.legacyStatus === 'CANCELED') {
    return deny('subscription_inactive', 402, legacyGate ? 'A assinatura desta empresa está suspensa ou cancelada. O histórico continua disponível para leitura.' : COMMERCIAL_MESSAGES.subscription_inactive_legacy);
  }
  if (COMMERCIAL_BLOCKING.includes(status)) return deny('subscription_inactive', 402, blockedMessage(status));
  if (rule.mode === 'INDISPONIVEL') return deny(rule.origin === 'LEGADO' ? 'not_entitled' : 'feature_unavailable', 403, rule.reason ?? `${f.label} não está incluída no plano da sua empresa.`);
  if (rule.mode === 'LIMITADO' && usage && rule.limit !== null && usage.used >= rule.limit) return deny('quota_exceeded', 429, quotaMessage(rule, f));
  if (rule.mode === 'CREDITOS' && credits && credits.available < (rule.credits ?? 0)) return deny('insufficient_credits', 402, COMMERCIAL_MESSAGES.insufficient_credits);
  return out(true, null, 200, null);
}

// ── Assentos (IM-SEC-128) ────────────────────────────────────────────────────────────────────────────────────────
/**
 * Regra única de contagem: ocupa assento o vínculo ATIVO (um por pessoa na organização — chave da tabela); convite
 * pendente (não aceito, não revogado, não vencido) RESERVA assento; vínculo desativado ou encerrado não ocupa.
 * Limite = usuários contratados na assinatura (ajuste administrativo) ou, sem ajuste, o incluído na versão do plano.
 */
export function seatLimitOf(sub: Pick<SubscriptionState, 'seatsContracted' | 'plan'>): number | null {
  if (sub.seatsContracted !== null) return sub.seatsContracted;
  if (!sub.plan || sub.plan.seatsUnlimited) return null;
  return sub.plan.seatsIncluded;
}

export interface SeatSummary { limit: number | null; active: number; pending: number; used: number; available: number | null; excess: number }
export function seatSummary(limit: number | null, active: number, pending: number): SeatSummary {
  const used = active + pending;
  return { limit, active, pending, used, available: limit === null ? null : Math.max(0, limit - used), excess: limit === null ? 0 : Math.max(0, active - limit) };
}

// ── Versões: validação e diferenças ─────────────────────────────────────────────────────────────────────────────────
export interface DraftShape {
  seatsIncluded: number | null;
  seatsUnlimited: boolean;
  creditPolicy: CreditPolicy;
  features: Record<string, FeatureRule>;
}

/** Pendências que impedem publicar (matriz completa e coerente; nada fica implícito). */
export function publishIssues(d: DraftShape): string[] {
  const issues: string[] = [];
  if (!d.seatsUnlimited && (d.seatsIncluded === null || d.seatsIncluded < 1)) issues.push('Defina a quantidade de usuários incluídos ou marque "sem limite de usuários".');
  if (d.seatsUnlimited && d.seatsIncluded !== null) issues.push('Usuários incluídos e "sem limite" não podem valer ao mesmo tempo.');
  if (d.creditPolicy.creditsPerPeriod !== null && (!Number.isInteger(d.creditPolicy.creditsPerPeriod) || d.creditPolicy.creditsPerPeriod < 1)) issues.push('A concessão de créditos por mês deve ser um número inteiro positivo (ou vazia).');
  for (const f of COMMERCIAL_FEATURES) {
    const r = d.features[f.key];
    if (!r) { issues.push(`${f.label}: defina a regra.`); continue; }
    issues.push(...ruleIssues(f, r).map((m) => `${f.label}: ${m}`));
  }
  for (const k of Object.keys(d.features)) if (!FEATURE_BY_KEY[k]) issues.push(`Funcionalidade desconhecida: ${k}.`);
  const usesCredits = Object.values(d.features).some((r) => r.mode === 'CREDITOS');
  if (usesCredits && d.creditPolicy.creditsPerPeriod === null) issues.push('Há funcionalidades mediante créditos: defina a concessão mensal (os créditos avulsos são lançados pela administração).');
  return issues;
}

export function ruleIssues(f: CommercialFeature, r: FeatureRule): string[] {
  const k = KIND_RULES[f.kind];
  const out: string[] = [];
  if (!k.modes.includes(r.mode)) { out.push(`modo "${FEATURE_MODE_LABEL[r.mode] ?? r.mode}" não se aplica.`); return out; }
  if (r.mode === 'LIMITADO') {
    if (r.limit === null || !Number.isInteger(r.limit) || r.limit < 0) out.push('informe o limite (inteiro, zero ou maior).');
    if (!r.period || !k.periods.includes(r.period)) out.push(`período inválido (use ${k.periods.map((p) => LIMIT_PERIOD_LABEL[p]).join(' ou ')}).`);
  } else if (r.limit !== null || r.period !== null) out.push('limite só vale no modo "Disponível com limite".');
  if (r.mode === 'CREDITOS') { if (r.credits === null || !Number.isInteger(r.credits) || r.credits < 1) out.push('informe os créditos por uso (inteiro positivo).'); }
  else if (r.credits !== null) out.push('créditos por uso só valem no modo "Disponível mediante créditos".');
  return out;
}

export type DiffKind = 'AMPLIA' | 'REDUZ' | 'IGUAL' | 'ALTERA';
export interface RuleDiff { key: string; label: string; from: FeatureRule | null; to: FeatureRule | null; kind: DiffKind }

/** Ordem de "generosidade" para classificar mudanças (dentro do mesmo modo compara o número). */
const MODE_RANK: Record<FeatureMode, number> = { INDISPONIVEL: 0, CREDITOS: 1, LIMITADO: 1, DISPONIVEL: 3 };
function compareRule(a: FeatureRule | null, b: FeatureRule | null): DiffKind {
  if (!a && !b) return 'IGUAL';
  if (!a) return b!.mode === 'INDISPONIVEL' ? 'IGUAL' : 'AMPLIA';
  if (!b) return a.mode === 'INDISPONIVEL' ? 'IGUAL' : 'REDUZ';
  if (a.mode === b.mode && a.limit === b.limit && a.period === b.period && a.credits === b.credits) return 'IGUAL';
  if (a.mode === b.mode && a.mode === 'LIMITADO' && a.period === b.period) return (b.limit ?? 0) > (a.limit ?? 0) ? 'AMPLIA' : 'REDUZ';
  if (a.mode === b.mode && a.mode === 'CREDITOS') return (b.credits ?? 0) < (a.credits ?? 0) ? 'AMPLIA' : 'REDUZ';
  if (MODE_RANK[a.mode] !== MODE_RANK[b.mode]) return MODE_RANK[b.mode] > MODE_RANK[a.mode] ? 'AMPLIA' : 'REDUZ';
  return 'ALTERA';
}

/** Diferenças entre a regra vigente (plano ou legado) e a de destino, por funcionalidade do catálogo. */
export function diffRules(from: (key: string) => FeatureRule | null, to: (key: string) => FeatureRule | null): RuleDiff[] {
  return COMMERCIAL_FEATURES.map((f) => {
    const a = from(f.key); const b = to(f.key);
    return { key: f.key, label: f.label, from: a, to: b, kind: compareRule(a, b) };
  });
}

export const describeRule = (r: FeatureRule | null): string => {
  if (!r) return 'Não definido';
  if (r.mode === 'LIMITADO') return `${r.limit ?? '—'} ${r.period ? LIMIT_PERIOD_LABEL[r.period] : ''}`.trim();
  if (r.mode === 'CREDITOS') return `${r.credits ?? '—'} crédito(s) por uso`;
  return FEATURE_MODE_LABEL[r.mode];
};

// ── Créditos ─────────────────────────────────────────────────────────────────────────────────────────────────────
export const CREDIT_KINDS = ['CONCESSAO', 'CONSUMO', 'ESTORNO', 'AJUSTE', 'EXPIRACAO'] as const;
export type CreditKind = (typeof CREDIT_KINDS)[number];
export const CREDIT_KIND_LABEL: Record<CreditKind, string> = { CONCESSAO: 'Concessão', CONSUMO: 'Consumo', ESTORNO: 'Estorno', AJUSTE: 'Ajuste administrativo', EXPIRACAO: 'Expiração' };
export const BILLING_STATUS_LABEL: Record<string, string> = {
  NAO_APLICADO: 'Sem cobrança', RESERVADO: 'Créditos reservados', CONSUMIDO: 'Créditos consumidos', LIBERADO: 'Reserva liberada', ESTORNADO: 'Estornado',
};
export const USAGE_STATUS_LABEL: Record<string, string> = {
  REQUESTED: 'Solicitada', RUNNING: 'Em execução', SUCCEEDED: 'Concluída', PARTIAL: 'Concluída parcialmente', FAILED: 'Falhou', CANCELED: 'Cancelada',
};

// ── Requisições (validação no servidor) ─────────────────────────────────────────────────────────────────────────────
const TotpCode = z.string().trim().regex(/^\d{6}$/, 'Informe os 6 dígitos do aplicativo autenticador.');
const Reason = z.string().trim().min(5, 'Explique o motivo (mínimo de 5 caracteres).').max(500);
const PlanCode = z.string().trim().toLowerCase().regex(/^[a-z][a-z0-9-]{2,39}$/, 'Identificador: 3 a 40 caracteres (letras minúsculas, números e hífen), começando por letra.');
const Name = z.string().trim().min(2).max(80);
const Description = z.string().trim().max(1000).default('');
const Int = (min: number, max: number) => z.number().int().min(min).max(max);

export const FeatureRuleInput = z.object({
  mode: z.enum(FEATURE_MODES),
  limit: Int(0, 1_000_000).nullable().default(null),
  period: z.enum(LIMIT_PERIODS).nullable().default(null),
  credits: Int(1, 1_000_000).nullable().default(null),
}).strict();

export const CreateCommercialPlanRequest = z.object({ code: PlanCode, name: Name, description: Description, category: z.enum(COMMERCIAL_PLAN_CATEGORIES) }).strict();
export const SaveCommercialPlanDraftRequest = z.object({
  name: Name, description: Description,
  seatsIncluded: Int(1, 100_000).nullable(), seatsUnlimited: z.boolean(),
  creditsPerPeriod: Int(1, 100_000_000).nullable(), creditsExpireAtPeriodEnd: z.boolean(),
  features: z.record(z.string().regex(/^[a-z_]{3,40}$/), FeatureRuleInput),
  changeSummary: z.string().trim().max(500).default(''),
  /** Revisão lida (concorrência otimista: duas pessoas editando o mesmo rascunho). */
  revision: Int(0, 1_000_000),
}).strict();
export const PublishCommercialPlanVersionRequest = z.object({ reason: Reason, code: TotpCode }).strict();
export const NewCommercialPlanVersionRequest = z.object({ reason: Reason }).strict();
export const DuplicateCommercialPlanRequest = z.object({ code: PlanCode, name: Name, reason: Reason }).strict();
export const CommercialPlanStatusRequest = z.object({ status: z.enum(COMMERCIAL_PLAN_STATUSES), reason: Reason, code: TotpCode }).strict();

export const ChangeSubscriptionRequest = z.object({
  /** null = voltar (ou permanecer) em LEGADO. */
  planVersionId: z.string().uuid().nullable(),
  commercialStatus: z.enum(COMMERCIAL_STATUSES),
  seatsContracted: Int(1, 100_000).nullable(),
  endsAt: z.string().datetime({ offset: true }).nullable(),
  reason: Reason,
  /** Identificador (slug) da organização, digitado. */
  confirm: z.string().trim().min(1).max(120),
  code: TotpCode,
  /** Revisão lida da assinatura (recusa se outra pessoa alterou antes). */
  revision: Int(0, 1_000_000),
}).strict().superRefine((v, ctx) => {
  if ((v.planVersionId === null) !== (v.commercialStatus === 'LEGADO')) ctx.addIssue({ code: 'custom', path: ['commercialStatus'], message: 'LEGADO vale só sem plano; com plano, escolha outra situação.' });
  if (v.commercialStatus === 'EXPIRADA') ctx.addIssue({ code: 'custom', path: ['commercialStatus'], message: 'Expirada é calculada pela data de término; informe a data em vez da situação.' });
});
export const SubscriptionPreviewQuery = z.object({ planVersionId: z.string().uuid().optional(), seatsContracted: z.coerce.number().int().min(1).max(100_000).optional() }).strict();

export const CreditAdjustRequest = z.object({
  kind: z.enum(['CONCESSAO', 'AJUSTE']),
  amount: z.number().int().min(-1_000_000).max(1_000_000).refine((n) => n !== 0, 'Informe uma quantidade diferente de zero.'),
  expiresAt: z.string().datetime({ offset: true }).nullable().default(null),
  reason: Reason,
  code: TotpCode,
  idempotencyKey: z.string().regex(/^[A-Za-z0-9._:-]{8,128}$/),
}).strict().superRefine((v, ctx) => {
  if (v.kind === 'CONCESSAO' && v.amount < 0) ctx.addIssue({ code: 'custom', path: ['amount'], message: 'Concessão é sempre positiva; para reduzir, use ajuste.' });
  if (v.amount < 0 && v.expiresAt) ctx.addIssue({ code: 'custom', path: ['expiresAt'], message: 'Validade só se aplica a créditos acrescentados.' });
});
export const CreditRefundRequest = z.object({ usageId: z.string().uuid(), reason: Reason, code: TotpCode }).strict();

export const UsageQuery = z.object({
  feature: z.string().regex(/^[a-z_]{3,40}$/).optional(),
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(30),
  offset: z.coerce.number().int().min(0).max(100_000).default(0),
}).strict();
export const LedgerQuery = z.object({ limit: z.coerce.number().int().min(1).max(100).default(30), offset: z.coerce.number().int().min(0).max(100_000).default(0) }).strict();

// ── DTOs ─────────────────────────────────────────────────────────────────────────────────────────────────────────
export interface CommercialFeatureDto {
  key: string; category: FeatureCategory; kind: FeatureKind; label: string; description: string; costDriver: CommercialFeature['costDriver'];
  unit: CommercialFeature['unit']; instrumented: boolean; modes: readonly FeatureMode[]; periods: readonly LimitPeriod[];
  /** Regra que vale para organizações LEGADO (texto). */
  legacyRule: string;
}

export interface CommercialPlanVersionDto {
  id: string; versionNo: number; status: CommercialPlanVersionStatus; revision: number;
  name: string; description: string;
  seatsIncluded: number | null; seatsUnlimited: boolean; creditPolicy: CreditPolicy;
  features: Record<string, FeatureRule>;
  changeSummary: string;
  createdAt: string; publishedAt: string | null;
  /** Assinaturas vigentes nesta versão. */
  subscriptions: number;
  issues: string[];
  /** SAAS-006: condição financeira da própria versão; ausente só em fixtures anteriores. */
  pricing?: import('./billing.js').PlanPricingDto;
}
export interface CommercialPlanSummaryDto {
  id: string; code: string; category: CommercialPlanCategory; status: CommercialPlanStatus; name: string; description: string;
  createdAt: string; latestPublished: number | null; hasDraft: boolean; subscriptions: number; versionCount: number;
}
export interface CommercialPlanEventDto { id: string; kind: string; versionNo: number | null; reason: string | null; actorName: string | null; at: string; details: Record<string, unknown> }
export interface CommercialPlanDetailDto extends CommercialPlanSummaryDto { versions: CommercialPlanVersionDto[]; history: CommercialPlanEventDto[] }

export interface FeatureUsageDto {
  key: string; label: string; category: FeatureCategory; kind: FeatureKind;
  mode: FeatureMode; origin: 'LEGADO' | 'PLANO'; limit: number | null; period: LimitPeriod | null; credits: number | null;
  /** Uso no período corrente (LIMITADO) ou quantidade atual (CAPACIDADE); null quando não se aplica. */
  used: number | null; remaining: number | null; renewsAt: string | null;
  /** Instrumentado nesta versão; false = regra registrada, medição pendente. */
  instrumented: boolean;
}

export interface CreditBalanceDto { available: number; reserved: number; balance: number; expiringIn30Days: number; nextExpiration: string | null }

export interface SubscriptionEventDto {
  id: string; kind: string; fromStatus: string | null; toStatus: string | null; fromPlan: string | null; toPlan: string | null;
  fromSeats: number | null; toSeats: number | null; reason: string | null; actorName: string | null; actorScope: string; at: string;
}

/** IM-API-387 (ampliada na SAAS-005): plano, recursos, assentos, consumo do período e créditos — só dados reais. */
export interface TenantSubscriptionDto {
  commercialStatus: CommercialStatus;
  registeredStatus: string | null;
  planCode: string | null;
  startedAt: string | null;
  seats: { active: number; inactive: number; pendingInvitations: number; contracted: number | null; available: number | null; limit: number | null; excess: number };
  plan: { code: string; name: string; category: CommercialPlanCategory; versionNo: number } | null;
  endsAt: string | null;
  features: FeatureUsageDto[];
  credits: CreditBalanceDto | null;
  history: SubscriptionEventDto[];
  timezone: string;
}

export interface UsageRecordDto {
  id: string; feature: string; featureLabel: string; status: string; billingStatus: string; quantity: number; unit: string;
  creditsCharged: number; creditsReserved: number; source: string; mode: string | null; userName: string | null;
  createdAt: string; finishedAt: string | null; operationalCost: { amount: number; currency: string; source: string } | null;
}

export interface CreditLedgerEntryDto {
  id: string; kind: CreditKind; amount: number; expiresAt: string | null; reason: string | null; usageId: string | null;
  actorScope: string; actorName: string | null; at: string;
}

export interface PlatformCommercialOrgRow {
  id: string; slug: string; name: string; purpose: 'REAL' | 'TECNICO' | 'DEMONSTRACAO'; operationalStatus: 'ACTIVE' | 'SUSPENDED';
  commercialStatus: CommercialStatus; legacyStatus: string | null; planCode: string | null; planName: string | null; versionNo: number | null;
  seatsLimit: number | null; seatsActive: number; seatsPending: number; creditsAvailable: number; usageLast30Days: number;
}

export interface PlatformCommercialOrgDto {
  organization: { id: string; slug: string; name: string; purpose: PlatformCommercialOrgRow['purpose']; operationalStatus: PlatformCommercialOrgRow['operationalStatus'] };
  subscription: {
    revision: number; commercialStatus: CommercialStatus; effectiveStatus: CommercialStatus; legacyStatus: string | null; legacyPlanCode: string | null;
    planVersionId: string | null; planId: string | null; planCode: string | null; planName: string | null; versionNo: number | null;
    seatsContracted: number | null; startedAt: string | null; endsAt: string | null; commercialSince: string | null;
  };
  seats: SeatSummary;
  features: FeatureUsageDto[];
  credits: CreditBalanceDto;
  history: SubscriptionEventDto[];
}

export interface SubscriptionPreviewDto {
  from: { commercialStatus: CommercialStatus; plan: string | null; seatsLimit: number | null };
  to: { plan: string | null; versionNo: number | null; seatsLimit: number | null; planStatus: CommercialPlanStatus | null };
  diff: RuleDiff[];
  seats: { active: number; pending: number; limitAfter: number | null; excessAfter: number };
  /** Capacidades que ficariam acima do novo limite (nada é apagado; só novas inclusões ficam bloqueadas). */
  capacityExcess: Array<{ key: string; label: string; current: number; limit: number }>;
  warnings: string[];
}

/** Corpos enviados pela interface (tipos de entrada dos esquemas acima). */
export type SaveDraftBody = z.input<typeof SaveCommercialPlanDraftRequest>;
export type ChangeSubscriptionBody = z.input<typeof ChangeSubscriptionRequest>;
